import type { ComponentType } from 'react'
import { memo, useEffect, useRef, useState } from 'react'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import { FOCUS_RING, FOCUS_RING_INSET } from '@/lib/focus-ring'
import { rewriteEntityTerms } from '@/lib/terminology/entity-terms'
import { cn } from '@/lib/utils'
import {
  ArrowSquareOutIcon,
  CalendarIcon,
  CaretDownIcon,
  CaretRightIcon,
  CheckSquareIcon,
  ClockIcon,
  CurrencyDollarIcon,
  EnvelopeSimpleIcon,
  EyeIcon,
  FileIcon,
  FunctionIcon,
  GlobeIcon,
  HashIcon,
  LinkSimpleIcon,
  ListBulletsIcon,
  ListChecksIcon,
  MagnifyingGlassIcon,
  MapPinIcon,
  PaperclipIcon,
  PercentIcon,
  PhoneIcon,
  SigmaIcon,
  SlidersHorizontalIcon,
  TextAlignLeftIcon,
  TextTIcon,
  UserIcon,
  UsersThreeIcon,
} from '@phosphor-icons/react'

import { TableCustomizerDialog } from './TableCustomizerDialog'

interface FieldTypeOption {
  value: string
  label: string
  description: string
  isPopular?: boolean
}

interface HiddenColumn {
  id: string
  label: string
  description?: string
}

interface ColumnItem {
  id: string
  label: string
  description?: string
  isVisible: boolean
  isFixed?: boolean
  isSystem?: boolean
  isCustomField?: boolean
  order?: number
}

interface CompactAddColumnDropdownProps {
  fieldTypeOptions?: FieldTypeOption[]
  hiddenColumns?: HiddenColumn[]
  onFieldTypeSelect?: (fieldType: string) => void
  onEnableColumn?: (columnId: string) => void
  persistKey?: string
  closeOnSelect?: boolean
  // Table customizer props
  allColumns?: ColumnItem[]
  onColumnVisibilityChange?: (
    columnId: string,
    isVisible: boolean,
  ) => Promise<void>
  onColumnsReorder?: (reorderedColumns: ColumnItem[]) => Promise<void>
  showCustomizeButton?: boolean
  canModifyColumns?: boolean
}

const tabStateByKey = new Map<string, 'new' | 'hidden'>()

// Category definitions with icons and colors
const FIELD_CATEGORIES = [
  {
    id: 'text',
    label: 'Text',
    types: ['STRING', 'TEXT', 'RICH_TEXT'],
    color: 'text-blue-500',
    bgColor: 'bg-blue-500/10',
  },
  {
    id: 'number',
    label: 'Number',
    types: ['NUMBER', 'PERCENTAGE', 'CURRENCY'],
    color: 'text-emerald-500',
    bgColor: 'bg-emerald-500/10',
  },
  {
    id: 'dateTime',
    label: 'Date & Time',
    types: ['DATE', 'DATETIME', 'TIME'],
    color: 'text-amber-500',
    bgColor: 'bg-amber-500/10',
  },
  {
    id: 'selection',
    label: 'Selection',
    types: ['DROPDOWN', 'MULTISELECT', 'BOOLEAN'],
    color: 'text-purple-500',
    bgColor: 'bg-purple-500/10',
  },
  {
    id: 'contact',
    label: 'Contact',
    types: ['EMAIL', 'PHONE', 'URL'],
    color: 'text-cyan-500',
    bgColor: 'bg-cyan-500/10',
  },
  {
    id: 'reference',
    label: 'Reference',
    types: ['REFERENCE', 'MULTI_REFERENCE', 'USER', 'MULTI_USER'],
    color: 'text-pink-500',
    bgColor: 'bg-pink-500/10',
  },
  {
    id: 'files',
    label: 'Files',
    types: ['FILE', 'ATTACHMENTS'],
    color: 'text-orange-500',
    bgColor: 'bg-orange-500/10',
  },
  {
    id: 'other',
    label: 'Other',
    types: ['LINK', 'ADDRESS'],
    color: 'text-slate-500',
    bgColor: 'bg-slate-500/10',
  },
  {
    id: 'calculated',
    label: 'Calculated',
    types: ['FORMULA', 'LOOKUP', 'ROLLUP'],
    color: 'text-violet-500',
    bgColor: 'bg-violet-500/10',
  },
]

// Function to get icon for field type with better visual design
const getFieldTypeIcon = (
  fieldType: string,
): {
  Icon: ComponentType<{ className?: string; weight?: string }>
  color: string
  bgColor: string
} => {
  const upperType = fieldType.toUpperCase()

  // Find the category for this type
  const category = FIELD_CATEGORIES.find(cat => cat.types.includes(upperType))
  const color = category?.color || 'text-slate-500'
  const bgColor = category?.bgColor || 'bg-slate-500/10'

  let Icon: ComponentType<{ className?: string; weight?: string }>

  switch (upperType) {
    case 'STRING':
      Icon = TextTIcon
      break
    case 'TEXT':
    case 'RICH_TEXT':
      Icon = TextAlignLeftIcon
      break
    case 'NUMBER':
      Icon = HashIcon
      break
    case 'PERCENTAGE':
      Icon = PercentIcon
      break
    case 'CURRENCY':
      Icon = CurrencyDollarIcon
      break
    case 'DATE':
    case 'DATETIME':
      Icon = CalendarIcon
      break
    case 'TIME':
      Icon = ClockIcon
      break
    case 'DROPDOWN':
      Icon = ListBulletsIcon
      break
    case 'MULTISELECT':
      Icon = ListChecksIcon
      break
    case 'BOOLEAN':
      Icon = CheckSquareIcon
      break
    case 'REFERENCE':
      Icon = LinkSimpleIcon
      break
    case 'MULTI_REFERENCE':
      Icon = UsersThreeIcon
      break
    case 'USER':
      Icon = UserIcon
      break
    case 'MULTI_USER':
      Icon = UsersThreeIcon
      break
    case 'FILE':
      Icon = FileIcon
      break
    case 'ATTACHMENTS':
      Icon = PaperclipIcon
      break
    case 'LINK':
      Icon = LinkSimpleIcon
      break
    case 'ADDRESS':
      Icon = MapPinIcon
      break
    case 'EMAIL':
      Icon = EnvelopeSimpleIcon
      break
    case 'PHONE':
      Icon = PhoneIcon
      break
    case 'URL':
      Icon = GlobeIcon
      break
    case 'FORMULA':
      Icon = FunctionIcon
      break
    case 'LOOKUP':
      Icon = ArrowSquareOutIcon
      break
    case 'ROLLUP':
      Icon = SigmaIcon
      break
    default:
      Icon = CaretDownIcon
  }

  return { Icon, color, bgColor }
}

// Field type item component with hover effect and description
function FieldTypeItem({
  fieldType,
  onClick,
}: {
  fieldType: FieldTypeOption
  onClick: () => void
}) {
  const { Icon, color, bgColor } = getFieldTypeIcon(fieldType.value)

  return (
    <button
      onClick={onClick}
      className={cn(
        'flex w-full items-start gap-3 rounded-lg px-3 py-2.5 text-left',
        'transition-tonal duration-150 hover:bg-accent/50',
        'group cursor-pointer',
      )}
    >
      <div
        className={cn(
          'flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg',
          bgColor,
          'transition-transform duration-150 group-hover:scale-105',
        )}
      >
        <Icon className={cn('h-4 w-4', color)} weight="duotone" />
      </div>
      <div className="min-w-0 flex-1 pt-0.5">
        <div className="text-sm font-medium text-foreground transition-colors group-hover:text-primary">
          {fieldType.label}
        </div>
        <div className="mt-0.5 line-clamp-1 text-xs text-muted-foreground">
          {rewriteEntityTerms(fieldType.description)}
        </div>
      </div>
    </button>
  )
}

// Hidden column item component
function HiddenColumnItem({
  column,
  onClick,
}: {
  column: HiddenColumn
  onClick: () => void
}) {
  return (
    <button
      onClick={onClick}
      className={cn(
        'flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-left',
        'transition-tonal duration-150 hover:bg-accent/50',
        'group cursor-pointer',
      )}
    >
      <div
        className={cn(
          'flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg',
          'bg-muted',
          'transition-colors duration-150 group-hover:bg-primary/10',
        )}
      >
        <EyeIcon
          className="h-4 w-4 text-muted-foreground transition-colors group-hover:text-primary"
          weight="duotone"
        />
      </div>
      <div className="min-w-0 flex-1">
        <div className="truncate text-sm font-medium text-foreground transition-colors group-hover:text-primary">
          {column.label}
        </div>
        {column.description && (
          <div className="mt-0.5 line-clamp-1 text-xs text-muted-foreground">
            {column.description}
          </div>
        )}
      </div>
    </button>
  )
}

function CompactAddColumnDropdownComponent({
  fieldTypeOptions = [],
  hiddenColumns = [],
  onFieldTypeSelect,
  onEnableColumn,
  persistKey,
  closeOnSelect = true,
  // Table customizer props
  allColumns = [],
  onColumnVisibilityChange,
  onColumnsReorder,
  showCustomizeButton = false,
  canModifyColumns = true,
}: CompactAddColumnDropdownProps) {
  const key = persistKey || 'default'
  const [activeTab, setActiveTab] = useState<'new' | 'hidden'>(
    () => tabStateByKey.get(key) ?? 'new',
  )
  const [open, setOpen] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')
  const [customizerOpen, setCustomizerOpen] = useState(false)
  // Force update counter for when we need to re-render after ref changes
  const [, forceUpdate] = useState(0)

  // Cache values in refs when popover opens to prevent content shifting
  const cachedFieldTypeOptionsRef = useRef(fieldTypeOptions)
  const cachedHiddenColumnsRef = useRef(hiddenColumns)
  const prevOpenRef = useRef(open)

  // Update cache when popover opens (not on every render)
  if (open && !prevOpenRef.current) {
    cachedFieldTypeOptionsRef.current = fieldTypeOptions
    cachedHiddenColumnsRef.current = hiddenColumns
  }
  prevOpenRef.current = open

  // Use cached values when open, current props when closed
  const renderFieldTypeOptions = open
    ? cachedFieldTypeOptionsRef.current
    : fieldTypeOptions
  const renderHiddenColumns = open
    ? cachedHiddenColumnsRef.current
    : hiddenColumns
  const sortedHiddenColumns = [...renderHiddenColumns].sort((a, b) =>
    a.label.localeCompare(b.label, undefined, {
      numeric: true,
      sensitivity: 'base',
    }),
  )

  useEffect(() => {
    const nextActiveTab = tabStateByKey.get(key) ?? 'new'

    setOpen(false)
    setActiveTab(currentTab =>
      currentTab === nextActiveTab ? currentTab : nextActiveTab,
    )
  }, [key])

  const hasHiddenColumns = sortedHiddenColumns.length > 0
  const hasFieldTypeOptions = renderFieldTypeOptions.length > 0
  const showHiddenOnly = !hasFieldTypeOptions && hasHiddenColumns
  const effectiveTab = showHiddenOnly ? 'hidden' : activeTab

  useEffect(() => {
    if (!hasHiddenColumns && activeTab === 'hidden') {
      setActiveTab('new')
      tabStateByKey.set(key, 'new')
    }
  }, [activeTab, hasHiddenColumns, key])

  useEffect(() => {
    if (showHiddenOnly && activeTab !== 'hidden') {
      setActiveTab('hidden')
      tabStateByKey.set(key, 'hidden')
    }
  }, [activeTab, key, showHiddenOnly])

  const normalizedQuery = searchQuery.trim().toLowerCase()
  const matchesQuery = (label: string, description?: string) =>
    !normalizedQuery ||
    label.toLowerCase().includes(normalizedQuery) ||
    (description?.toLowerCase().includes(normalizedQuery) ?? false)

  // Group field types by category
  const groupedFieldTypes = FIELD_CATEGORIES.map(category => ({
    ...category,
    fields: renderFieldTypeOptions.filter(
      opt =>
        category.types.includes(opt.value.toUpperCase()) &&
        matchesQuery(opt.label, opt.description),
    ),
  })).filter(category => category.fields.length > 0)

  const filteredHiddenColumns = sortedHiddenColumns.filter(column =>
    matchesQuery(column.label, column.description),
  )

  const handleOpenChange = (nextOpen: boolean) => {
    if (nextOpen === open) {
      return
    }

    setOpen(nextOpen)
    if (nextOpen) {
      setSearchQuery('')
    }
  }

  const handleTabChange = (nextTab: 'new' | 'hidden') => {
    setActiveTab(nextTab)
    tabStateByKey.set(key, nextTab)
  }

  const handleFieldTypeSelect = (fieldType: string) => {
    onFieldTypeSelect?.(fieldType)
    if (closeOnSelect) {
      handleOpenChange(false)
    }
  }

  const handleEnableColumn = (columnId: string) => {
    onEnableColumn?.(columnId)
    // Update cached ref for immediate feedback while popover is open
    cachedHiddenColumnsRef.current = cachedHiddenColumnsRef.current.filter(
      column => column.id !== columnId,
    )
    if (closeOnSelect) {
      handleOpenChange(false)
    } else {
      // Force re-render to show updated list when not closing
      forceUpdate(n => n + 1)
    }
  }

  const handleOpenCustomizer = () => {
    handleOpenChange(false) // Close the popover
    // Delay dialog opening so the Popover's DismissableLayer fully unmounts
    // before the Dialog's DismissableLayer mounts. The popover exit animation
    // takes ~150ms, during which its DismissableLayer stays registered.
    setTimeout(() => {
      setCustomizerOpen(true)
    }, 200)
  }

  const handleColumnVisibilityChange = async (
    columnId: string,
    isVisible: boolean,
  ) => {
    if (onColumnVisibilityChange) {
      await onColumnVisibilityChange(columnId, isVisible)
    }
  }

  const handleColumnsReorder = async (reorderedColumns: ColumnItem[]) => {
    if (onColumnsReorder) {
      await onColumnsReorder(reorderedColumns)
    }
  }

  // Determine if we can show the customizer
  const canShowCustomizer =
    showCustomizeButton &&
    allColumns.length > 0 &&
    onColumnVisibilityChange &&
    onColumnsReorder

  return (
    <Popover open={open} onOpenChange={handleOpenChange} modal={true}>
      <PopoverTrigger
        className={cn(
          'inline-flex h-6 w-6 items-center justify-center rounded-md p-0',
          'text-muted-foreground transition-colors hover:bg-primary/10 hover:text-primary',
          FOCUS_RING,
        )}
        title="Customize columns"
        aria-label="Customize columns"
      >
        {/* Sliders, not a plus: the popover adds fields, restores hidden
            columns, and opens the full customizer — and on customize-only
            tables (no field types, no hidden columns) "add" was never what
            it did. Matches the Customize Table icon inside the popover. */}
        <SlidersHorizontalIcon className="h-3.5 w-3.5" weight="bold" />
      </PopoverTrigger>
      <PopoverContent
        align="end"
        sideOffset={8}
        className={cn(
          'w-72 border-border/50 p-0 shadow-xl',
          'bg-popover',
          'rounded-card overflow-hidden',
        )}
      >
        {/* Header with tabs */}
        <div className="px-1 pb-0 pt-1">
          <div
            className={cn(
              'flex rounded-lg bg-muted/50 p-1',
              (!hasHiddenColumns || !hasFieldTypeOptions) && 'hidden',
            )}
          >
            <button
              onClick={() => handleTabChange('new')}
              className={cn(
                'transition-tonal flex-1 rounded-md px-3 py-1.5 text-xs font-medium duration-150',
                activeTab === 'new'
                  ? 'bg-background text-foreground shadow-sm'
                  : 'text-muted-foreground hover:text-foreground',
              )}
            >
              New Field
            </button>
            <button
              onClick={() => handleTabChange('hidden')}
              className={cn(
                'transition-tonal flex flex-1 items-center justify-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-medium duration-150',
                activeTab === 'hidden'
                  ? 'bg-background text-foreground shadow-sm'
                  : 'text-muted-foreground hover:text-foreground',
              )}
            >
              Existing
              <span
                className={cn(
                  'rounded-full px-1.5 py-0.5 text-[10px] font-semibold',
                  activeTab === 'hidden'
                    ? 'bg-primary/10 text-primary'
                    : 'bg-muted text-muted-foreground',
                )}
              >
                {sortedHiddenColumns.length}
              </span>
            </button>
          </div>
        </div>

        {/* Search filter */}
        {(hasFieldTypeOptions || hasHiddenColumns) && (
          <div className="px-3 pb-1 pt-2">
            <div className="relative">
              <MagnifyingGlassIcon className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
              <input
                type="text"
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                placeholder={
                  effectiveTab === 'new'
                    ? 'Search field types...'
                    : 'Search columns...'
                }
                className={cn(
                  'h-8 w-full rounded-lg border border-border/50 bg-muted/40 pl-8 pr-3 text-xs',
                  'text-foreground placeholder:text-muted-foreground',
                  'transition-colors focus:border-primary/40 focus:bg-background',
                  FOCUS_RING_INSET,
                )}
              />
            </div>
          </div>
        )}

        {/* Content — collapses entirely when there are no field types to add
            and no hidden columns (e.g. customize-only tables) */}
        <div
          style={{
            height: hasFieldTypeOptions || hasHiddenColumns ? 340 : 0,
            overflowY: 'auto',
            overscrollBehavior: 'contain',
          }}
          onWheel={e => e.stopPropagation()}
        >
          {effectiveTab === 'new' && hasFieldTypeOptions && (
            <div className="p-2">
              {!hasHiddenColumns && (
                <div className="mb-1 px-3 py-2">
                  <h4 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                    Add New Field
                  </h4>
                </div>
              )}

              {/* Grouped field types */}
              {groupedFieldTypes.length === 0 && (
                <div className="px-3 py-6 text-center text-xs text-muted-foreground">
                  No field types match &ldquo;{searchQuery.trim()}&rdquo;
                </div>
              )}
              {groupedFieldTypes.map((category, categoryIndex) => (
                <div
                  key={category.id}
                  className={cn(categoryIndex > 0 && 'mt-3')}
                >
                  <div className="px-3 pb-2 pt-1.5">
                    <span
                      className={cn(
                        'text-[10px] font-semibold uppercase tracking-wider',
                        category.color,
                      )}
                    >
                      {category.label}
                    </span>
                  </div>
                  <div className="space-y-0.5">
                    {category.fields.map(fieldType => (
                      <FieldTypeItem
                        key={fieldType.value}
                        fieldType={fieldType}
                        onClick={() => handleFieldTypeSelect(fieldType.value)}
                      />
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}

          {effectiveTab === 'hidden' && hasHiddenColumns && (
            <div className="p-2">
              <div className="mb-1 px-3 py-2">
                <h4 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                  Hidden Columns
                </h4>
                <p className="mt-0.5 text-[11px] text-muted-foreground">
                  Click to show column
                </p>
              </div>
              {filteredHiddenColumns.length === 0 && (
                <div className="px-3 py-6 text-center text-xs text-muted-foreground">
                  No columns match &ldquo;{searchQuery.trim()}&rdquo;
                </div>
              )}
              <div className="space-y-0.5">
                {filteredHiddenColumns.map(column => (
                  <HiddenColumnItem
                    key={`hidden-${column.id}`}
                    column={column}
                    onClick={() => handleEnableColumn(column.id)}
                  />
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Customize Table Button - at bottom */}
        {canShowCustomizer && (
          <>
            {(hasFieldTypeOptions || hasHiddenColumns) && (
              <div className="px-3 py-1">
                <div className="border-t border-border/50" />
              </div>
            )}
            <div className="px-2 pb-2 pt-1">
              <button
                onClick={handleOpenCustomizer}
                className={cn(
                  'flex w-full items-center gap-2.5 rounded-lg px-3 py-2.5 text-left',
                  'border border-primary/20 bg-primary/5 hover:border-primary/30 hover:bg-primary/10',
                  'transition-tonal group duration-150',
                )}
              >
                <div
                  className={cn(
                    'flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-md',
                    'bg-primary/10 transition-colors group-hover:bg-primary/20',
                  )}
                >
                  <SlidersHorizontalIcon
                    className="h-4 w-4 text-primary"
                    weight="duotone"
                  />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-semibold text-foreground">
                    Customize Table
                  </div>
                  <div className="text-xs text-muted-foreground">
                    Show, hide & reorder columns
                  </div>
                </div>
                <CaretRightIcon
                  className="h-4 w-4 flex-shrink-0 text-muted-foreground transition-transform duration-150 group-hover:translate-x-0.5 group-hover:text-primary"
                  weight="bold"
                />
              </button>
            </div>
          </>
        )}
      </PopoverContent>

      {/* Table Customizer Dialog */}
      {canShowCustomizer && (
        <TableCustomizerDialog
          open={customizerOpen}
          onOpenChange={setCustomizerOpen}
          columns={allColumns}
          onColumnVisibilityChange={handleColumnVisibilityChange}
          onColumnsReorder={handleColumnsReorder}
          fieldTypeOptions={renderFieldTypeOptions}
          onFieldTypeSelect={onFieldTypeSelect}
          canModify={canModifyColumns}
        />
      )}
    </Popover>
  )
}

// Memoize the component to prevent unnecessary re-renders when parent re-renders
export const CompactAddColumnDropdown = memo(
  CompactAddColumnDropdownComponent,
  (prevProps, nextProps) => {
    // Custom comparison to avoid re-renders from array reference changes
    const fieldTypeOptionsEqual =
      prevProps.fieldTypeOptions?.length ===
        nextProps.fieldTypeOptions?.length &&
      prevProps.fieldTypeOptions?.every(
        (opt, i) => opt.value === nextProps.fieldTypeOptions?.[i]?.value,
      )

    const hiddenColumnsEqual =
      prevProps.hiddenColumns?.length === nextProps.hiddenColumns?.length &&
      prevProps.hiddenColumns?.every(
        (col, i) => col.id === nextProps.hiddenColumns?.[i]?.id,
      )

    const allColumnsEqual =
      prevProps.allColumns?.length === nextProps.allColumns?.length &&
      prevProps.allColumns?.every(
        (col, i) =>
          col.id === nextProps.allColumns?.[i]?.id &&
          col.isVisible === nextProps.allColumns?.[i]?.isVisible,
      )

    return (
      fieldTypeOptionsEqual &&
      hiddenColumnsEqual &&
      allColumnsEqual &&
      prevProps.persistKey === nextProps.persistKey &&
      prevProps.closeOnSelect === nextProps.closeOnSelect &&
      prevProps.showCustomizeButton === nextProps.showCustomizeButton &&
      prevProps.canModifyColumns === nextProps.canModifyColumns &&
      prevProps.onFieldTypeSelect === nextProps.onFieldTypeSelect &&
      prevProps.onEnableColumn === nextProps.onEnableColumn &&
      prevProps.onColumnVisibilityChange ===
        nextProps.onColumnVisibilityChange &&
      prevProps.onColumnsReorder === nextProps.onColumnsReorder
    )
  },
)

CompactAddColumnDropdown.displayName = 'CompactAddColumnDropdown'
